// src/pages/finance-portal/journal-entries/JournalEntryForm.js
// QuickBooks-style journal entry form.
//
// Improvements over v1:
//   - Account Autocomplete shows account number, name, and type-color chip
//   - Entering a debit clears credit (and vice versa) on the same row
//   - Running totals footer: total DR, total CR, difference (colored)
//   - "Auto-balance" button: plugs the imbalance into a chosen account
//   - Keyboard: Enter on the last row adds a new row
//   - "Copy memo from line 1" quick-fill
//   - Save Draft vs Post Entry — Post is disabled until balanced

import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, CircularProgress, Autocomplete, Chip, Stack, Tooltip,
    MenuItem, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const TYPE_COLORS = {
    ASSET: '#22c55e', LIABILITY: '#ef4444', EQUITY: '#8b5cf6',
    REVENUE: '#3b82f6', EXPENSE: '#f59e0b',
};

const emptyLine = () => ({ account_id: '', debit_amount: '', credit_amount: '', memo: '' });

export default function JournalEntryForm({ open, onClose, onSaved, editEntry }) {
    const [accounts, setAccounts] = useState([]);
    const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState('');
    const [reference, setReference] = useState('');
    const [lines, setLines] = useState([emptyLine(), emptyLine()]);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [autoBalanceAccountId, setAutoBalanceAccountId] = useState('');

    const linesRef = useRef(null);

    useEffect(() => {
        if (open) {
            axiosInstance.get('/api/finance/coa', { params: { flat: true, status: 'ACTIVE' } })
                .then(res => {
                    if (res.data.success) {
                        setAccounts(res.data.data.filter(a => !a.is_header));
                    }
                }).catch(console.error);

            if (editEntry) {
                setEntryDate(editEntry.entry_date);
                setDescription(editEntry.description || '');
                setReference(editEntry.reference || '');
                setLines(editEntry.lines.map(l => ({
                    account_id: l.account_id,
                    debit_amount: l.debit_amount || '',
                    credit_amount: l.credit_amount || '',
                    memo: l.memo || '',
                })));
            } else {
                setEntryDate(new Date().toISOString().split('T')[0]);
                setDescription('');
                setReference('');
                setLines([emptyLine(), emptyLine()]);
            }
            setError(null);
            setAutoBalanceAccountId('');
        }
    }, [open, editEntry]);

    const updateLine = (index, field, value) => {
        const updated = [...lines];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'debit_amount' && value) updated[index].credit_amount = '';
        if (field === 'credit_amount' && value) updated[index].debit_amount = '';
        setLines(updated);
    };

    const addLine = () => setLines([...lines, emptyLine()]);
    const removeLine = (index) => {
        if (lines.length > 2) setLines(lines.filter((_, i) => i !== index));
    };

    const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit_amount) || 0), 0);
    const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit_amount) || 0), 0);
    const difference = +(totalDebit - totalCredit).toFixed(2);
    const absDiff = Math.abs(difference);
    const isBalanced = absDiff < 0.01;

    const autoBalance = () => {
        if (!autoBalanceAccountId || isBalanced) return;
        const newLine = {
            account_id: autoBalanceAccountId,
            debit_amount: difference < 0 ? absDiff.toFixed(2) : '',
            credit_amount: difference > 0 ? absDiff.toFixed(2) : '',
            memo: 'Auto-balance',
        };
        // If there's an empty line, fill it; else append
        const emptyIdx = lines.findIndex(
            l => !l.account_id && !l.debit_amount && !l.credit_amount
        );
        if (emptyIdx >= 0) {
            const next = [...lines];
            next[emptyIdx] = newLine;
            setLines(next);
        } else {
            setLines([...lines, newLine]);
        }
    };

    const copyMemoDown = () => {
        const firstMemo = lines[0]?.memo;
        if (!firstMemo) return;
        setLines(lines.map((l, i) => i === 0 ? l : { ...l, memo: firstMemo }));
    };

    const handleKeyDown = (e, index) => {
        // Enter on last row (memo field) adds a new row
        if (e.key === 'Enter' && index === lines.length - 1) {
            e.preventDefault();
            addLine();
        }
    };

    const formatCurrency = (val) => {
        const num = parseFloat(val);
        return isNaN(num) ? '0.00' : num.toFixed(2);
    };

    const handleSave = async (postImmediately = false) => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                entry_date: entryDate,
                description,
                reference,
                status: postImmediately ? 'POSTED' : 'DRAFT',
                lines: lines.filter(l => l.account_id).map(l => ({
                    account_id: l.account_id,
                    debit_amount: parseFloat(l.debit_amount) || 0,
                    credit_amount: parseFloat(l.credit_amount) || 0,
                    memo: l.memo,
                })),
            };
            if (editEntry) {
                await axiosInstance.put(`/api/finance/journal-entries/${editEntry.id}`, payload);
            } else {
                await axiosInstance.post('/api/finance/journal-entries', payload);
            }
            onSaved?.();
            onClose();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save journal entry';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
            }
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const renderAccountOption = (props, opt) => (
        <li {...props} key={opt.id}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 11, color: '#64748b', minWidth: 48 }}>
                    {opt.account_number}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: 13, flexGrow: 1 }}>
                    {opt.name}
                </Typography>
                <Chip
                    size="small"
                    label={opt.account_type?.substring(0, 3)}
                    sx={{
                        bgcolor: `${TYPE_COLORS[opt.account_type] || '#64748b'}15`,
                        color: TYPE_COLORS[opt.account_type] || '#64748b',
                        fontSize: 10, height: 18, fontWeight: 700,
                    }}
                />
            </Box>
        </li>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                {editEntry ? `Edit Journal Entry ${editEntry.entry_number}` : 'New Journal Entry'}
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

                {/* Header Fields */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        label="Entry Date" type="date" size="small" required
                        value={entryDate} onChange={e => setEntryDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ minWidth: 160 }}
                    />
                    <TextField
                        label="Description" size="small" fullWidth
                        value={description} onChange={e => setDescription(e.target.value)}
                        sx={{ minWidth: 300 }}
                    />
                    <TextField
                        label="Reference" size="small"
                        placeholder="Invoice #, PO#, doc ref"
                        value={reference} onChange={e => setReference(e.target.value)}
                        sx={{ minWidth: 160 }}
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Lines header */}
                <Box ref={linesRef} sx={{ overflowX: 'auto' }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '2.2fr 1fr 1fr 2fr 36px',
                        gap: 1, mb: 1, px: 1,
                    }}>
                        <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Account</Typography>
                        <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }} align="right">Debit</Typography>
                        <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }} align="right">Credit</Typography>
                        <Typography sx={{ fontFamily, fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Memo</Typography>
                        <Box />
                    </Box>

                    {lines.map((line, index) => {
                        const acct = accounts.find(a => a.id === line.account_id) || null;
                        return (
                            <Box key={index} sx={{
                                display: 'grid',
                                gridTemplateColumns: '2.2fr 1fr 1fr 2fr 36px',
                                gap: 1, mb: 1, alignItems: 'center',
                            }}>
                                <Autocomplete
                                    size="small"
                                    options={accounts}
                                    getOptionLabel={a => a ? `${a.account_number} — ${a.name}` : ''}
                                    renderOption={renderAccountOption}
                                    value={acct}
                                    onChange={(_, val) => updateLine(index, 'account_id', val?.id || '')}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderInput={(params) =>
                                        <TextField {...params} placeholder="Account…" />
                                    }
                                />
                                <TextField
                                    size="small" type="number" placeholder="0.00"
                                    value={line.debit_amount}
                                    onChange={e => updateLine(index, 'debit_amount', e.target.value)}
                                    inputProps={{ style: { textAlign: 'right' } }}
                                />
                                <TextField
                                    size="small" type="number" placeholder="0.00"
                                    value={line.credit_amount}
                                    onChange={e => updateLine(index, 'credit_amount', e.target.value)}
                                    inputProps={{ style: { textAlign: 'right' } }}
                                />
                                <TextField
                                    size="small" placeholder="Memo"
                                    value={line.memo}
                                    onChange={e => updateLine(index, 'memo', e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => removeLine(index)}
                                    disabled={lines.length <= 2}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>

                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<AddIcon />} onClick={addLine}
                            sx={{ fontFamily, textTransform: 'none', color: ACCENT }}>
                        Add Line
                    </Button>
                    <Tooltip title="Copy memo from line 1 to all other lines">
                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyMemoDown}
                                sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                            Copy memo down
                        </Button>
                    </Tooltip>
                </Stack>

                {/* Totals + auto-balance */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '2.2fr 1fr 1fr 2fr 36px',
                    gap: 1, mt: 2, p: 2, borderRadius: '10px',
                    bgcolor: isBalanced ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)',
                    border: `1px solid ${isBalanced ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                    alignItems: 'center',
                }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 12, color: '#64748b', textTransform: 'uppercase' }}>
                        Totals
                    </Typography>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 15 }} align="right">
                        ${formatCurrency(totalDebit)}
                    </Typography>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 15 }} align="right">
                        ${formatCurrency(totalCredit)}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontFamily, fontSize: 12, color: '#64748b', textTransform: 'uppercase' }}>
                            Diff
                        </Typography>
                        <Typography sx={{
                            fontFamily, fontWeight: 700, fontSize: 14,
                            color: isBalanced ? ACCENT : '#ef4444',
                        }}>
                            {isBalanced ? 'Balanced' : `$${formatCurrency(absDiff)}`}
                        </Typography>
                    </Stack>
                    <Box />
                </Box>

                {!isBalanced && (
                    <Box sx={{
                        display: 'flex', gap: 1, mt: 1, p: 1.5,
                        bgcolor: '#fffbeb', borderRadius: '8px',
                        border: '1px solid #fde68a',
                        alignItems: 'center', flexWrap: 'wrap',
                    }}>
                        <AutoFixHighIcon sx={{ color: '#d97706', fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontFamily, fontWeight: 600, color: '#92400e' }}>
                            Auto-balance ${formatCurrency(absDiff)} to
                        </Typography>
                        <Autocomplete
                            size="small"
                            options={accounts}
                            getOptionLabel={a => a ? `${a.account_number} — ${a.name}` : ''}
                            value={accounts.find(a => a.id === autoBalanceAccountId) || null}
                            onChange={(_, val) => setAutoBalanceAccountId(val?.id || '')}
                            renderOption={renderAccountOption}
                            sx={{ minWidth: 240, flexGrow: 1 }}
                            renderInput={(p) => <TextField {...p} placeholder="Pick plug account…" />}
                        />
                        <Button
                            size="small" variant="contained"
                            onClick={autoBalance}
                            disabled={!autoBalanceAccountId}
                            sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' },
                                  fontFamily, textTransform: 'none' }}
                        >
                            Apply
                        </Button>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Cancel
                </Button>
                <Button
                    variant="outlined" onClick={() => handleSave(false)} disabled={saving}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                          borderColor: ACCENT, color: ACCENT }}
                >
                    Save Draft
                </Button>
                <Button
                    variant="contained" onClick={() => handleSave(true)}
                    disabled={saving || !isBalanced}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                          bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    Post Entry
                </Button>
            </DialogActions>
        </Dialog>
    );
}
